<template>
	<div>
		<!-- Desktop Modal -->
		<b-modal
			id="loginModal"
			v-if="appBootstrapped && hasAccess() && !isMobile"
			v-model="showSignupModal"
			hide-header
			hide-footer
			centered
			body-class="p-0"
			no-close-on-backdrop
			no-close-on-esc
		>
			<LoginContent
				:visible="showSignupModal"
				initialMode="signup"
				:signinCheckData="signinCheckData"
				@close="showSignupModal = false"
			/>
		</b-modal>

		<!-- Mobile Bottom Sheet -->
		<div
			v-if="appBootstrapped && hasAccess() && isMobile && showSignupModal"
			class="gsf-mobile-modal-wrapper"
		>
			<transition name="gsf-fade" appear>
				<div class="gsf-mobile-modal-backdrop"></div>
			</transition>
			<transition name="gsf-slide-up" appear>
				<section class="gsf-mobile-container">
					<SignupForm
						:signinCheckData="signinCheckData"
						@next="showSignupModal = false"
					/>
				</section>
			</transition>
		</div>

		<!-- Onboarding Modal — opened after signup or when onboarding is incomplete -->
		<OnboardingModal
			v-if="showOnboardingModal"
			:initialStep="onboardingStep"
			:firstName="onboardingFirstName"
			@close="handleOnboardingClose"
		/>
	</div>
</template>

<script>
import Signup from "@/components/Onboarding/Signup.vue";
import SignupForm from "@/components/Onboarding/SignupForm.vue";
import LoginContent from "@/components/common/LoginContent.vue";
import OnboardingModal from "@/components/Onboarding/OnboardingModal.vue";
import EventBus from "@/utils/EventBus.js";
import { hasAccess } from "@/utils/tokenCheck.js";
import { mapGetters } from "vuex";

export default {
	name: "SignupModal",
	components: { Signup, SignupForm, LoginContent, OnboardingModal },
	props: {
		appBootstrapped: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			showSignupModal: false,
			showOnboardingModal: false,
			onboardingStep: null,
			onboardingFirstName: "",
			windowWidth: window.innerWidth,
			signinCheckData: null,
		};
	},
	watch: {
		showSignupModal(val) {
			this.$store.commit("candidateVUEX/setOnboardingActive", val);
		},
	},
	computed: {
		...mapGetters("userVUEX", {
			getResumeuuid: "getResumeUiid",
			getResumejobid: "getResumejobid",
			getDownloadReport: "getDownloadReport",
			getShowResumeClinic: "getShowResumeClinic",
		}),
		isMobile() {
			return this.windowWidth < 768;
		},
	},
	mounted() {
		window.addEventListener("resize", this.onResize);
		// Payload contains signinCheckData already fetched in loginMixin right after login
		EventBus.$on("show-signup-form", (payload) => {
			this.signinCheckData = payload?.signinCheckData ?? null;
			this.showSignupModal = true;
		});
		// payload may carry { step: 'skills' | 'goals' } for returning users
		EventBus.$on("show-onboarding-modal", (payload) => {
			this.onboardingStep = payload?.step ?? null;
			this.onboardingFirstName = payload?.firstName ?? "";
			if (payload?.step === "resumeUpload") {
				sessionStorage.setItem("fromOnboarding", "true");
			}
			this.showOnboardingModal = true;
		});
		// On refresh the router fires show-onboarding-modal before this component
		// is mounted, so the EventBus event is lost. Recover it from Vuex here.
		const pending = this.$store.getters["candidateVUEX/getPendingOnboarding"];
		if (pending !== null && pending !== undefined) {
			this.$store.commit("candidateVUEX/setPendingOnboarding", null);
			this.$nextTick(() => {
				this.onboardingStep = pending.step ?? null;
				this.onboardingFirstName = pending.firstName ?? "";
				if (pending.step === "resumeUpload") {
					sessionStorage.setItem("fromOnboarding", "true");
				}
				this.showOnboardingModal = true;
			});
		}
	},
	beforeDestroy() {
		window.removeEventListener("resize", this.onResize);
		EventBus.$off("show-signup-form");
		EventBus.$off("show-onboarding-modal");
	},
	methods: {
		hasAccess() {
			return hasAccess();
		},
		onResize() {
			this.windowWidth = window.innerWidth;
		},
		// Called when the new onboarding modal closes (skip / complete).
		// Mirrors the resume-clinic redirect logic in loginMixin.js so that
		// both the same-project flow (user clicked Resume Clinic while logged out,
		// registered, then completed onboarding) and the cross-project flow
		// (registered elsewhere and arrived here) open the clinic correctly.
		handleOnboardingClose() {
			this.showOnboardingModal = false;

			// ── Scenario 1: same-project Resume Clinic logged-out flow ──────────
			if (this.getResumeuuid) {
				const newUser = this.$store.getters["userVUEX/getNewUseruuid"];

				if (newUser) {
					this.$store.commit("userVUEX/setNewUseruuid", null);
					this.$store.commit("userVUEX/SET_RESUME_UUID", null);
					this.$store.commit("userVUEX/setresumeJobid", null);

					const query = { from: "candidate", fromWebsite: true };
					if (this.getDownloadReport) {
						query.downloadReport = true;
					} else {
						query.openResumeClinic = true;
					}
					this.$router.push({ path: "/dashboard", query }).catch(() => {});
					return;
				}

				if (this.getResumejobid) {
					const jobid = this.getResumejobid;
					const uuid = this.getResumeuuid;
					this.$store.commit("userVUEX/SET_RESUME_UUID", null);
					this.$store.commit("userVUEX/setresumeJobid", null);
					this.$router
						.push({
							path: "/job/search",
							query: {
								from: ["candidate", "resumeclinicModal"],
								tab: "hire",
								jobID: jobid,
								uuid: uuid,
								fromPage: this.$route.name,
							},
						})
						.catch(() => {});
					return;
				}

				// uuid only
				const uuid = this.getResumeuuid;
				this.$store.commit("userVUEX/SET_RESUME_UUID", null);
				this.$store.commit("userVUEX/setresumeJobid", null);
				const query = { from: "candidate", uuid };
				if (this.getDownloadReport) {
					this.$store.commit("userVUEX/setDownloadReport", false);
					query.downloadReport = true;
				} else {
					query.openResumeClinic = true;
				}
				this.$router.push({ path: "/dashboard", query }).catch(() => {});
				return;
			}

			// ── Scenario 2: cross-project registration ──────────────────────────
			// Already on the dashboard — router.push to the same route is a no-op
			// (NavigationDuplicated), so created/mounted never re-fire.
			// Emit onboarding-complete so CandidateDashboard re-runs its created
			// logic and opens the clinic when requested.
			if (this.getShowResumeClinic || this.getDownloadReport) {
				const isDownload = this.getDownloadReport;
				this.$store.commit("userVUEX/setShowResumeClinic", false);
				if (isDownload) {
					this.$store.commit("userVUEX/setDownloadReport", false);
				}
				EventBus.$emit("onboarding-complete", {
					openResumeClinic: !isDownload,
					downloadReport: isDownload,
				});
				return;
			}

			// ── Fallthrough: plain onboarding complete, no clinic needed ─────────
			EventBus.$emit("onboarding-complete", {
				openResumeClinic: false,
				downloadReport: false,
			});
		},
	},
};
</script>

<style>
.gsf-mobile-modal-wrapper {
	position: fixed;
	top: 0;
	left: 0;
	width: 100vw;
	height: 100dvh;
	z-index: 3000;
}
.gsf-mobile-modal-backdrop {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(27, 33, 44, 0.5);
	backdrop-filter: blur(4px);
}
.gsf-mobile-container {
	max-height: 520px;
	min-height: auto;
	background: #ffffff;
	border-radius: 40px 40px 0px 0px;
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	padding: 16px;
	z-index: 3001;
	padding-bottom: env(safe-area-inset-bottom, 0px) !important;
}
.gsf-close-bar-container {
	cursor: pointer;
}
.gsf-close-bar {
	width: 40px;
	height: 4px;
	background: #e2e8f0;
	border-radius: 2px;
}
.gsf-fade-enter-active,
.gsf-fade-leave-active {
	transition: opacity 0.3s ease;
}
.gsf-fade-enter,
.gsf-fade-leave-to {
	opacity: 0;
}
.gsf-slide-up-enter-active,
.gsf-slide-up-leave-active {
	transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}
.gsf-slide-up-enter,
.gsf-slide-up-leave-to {
	transform: translateY(100%);
	opacity: 0;
}
</style>
